import React from "react"
import { BLOCKS, MARKS, INLINES } from "@contentful/rich-text-types"
import { renderRichText } from "gatsby-source-contentful/rich-text"
import { Faq } from "../components/faqs"
import Image from "../images/image"
import Link from "./link"
import textToUrlPath from "./textToUrlPath"
import { isBrowser } from "./auth"
import VideoPlayer from "../components/video"
import { documentToReactComponents } from "@contentful/rich-text-react-renderer"
import FormModal from "../components/modal/FormModal"
import { Button } from "../components/button/button"

function getHeadingText(children) {
	let headingText = ""
	children &&
		children.forEach((child) => {
			try {
				if (typeof child === "object") {
					Object.entries(child).map((e) => {
						if (e[1]) {
							return (headingText += e[1].toString())
						} else {
							return (headingText += "")
						}
					})
				} else {
					child.map((node) => {
						if (node) {
							return (headingText += node)
						} else {
							return (headingText += "")
						}
					})
				}
			} catch (e) {
				console.log(e)
			}
		})
	return headingText
}

const Bold = ({ children }) => <span className="font-bold">{children}</span>
const H2 = ({ children }) => {
	const id = textToUrlPath(getHeadingText(children))

	return (
		<h2
			id={"anchor-" + id}
			className="group block -ml-10 pl-10"
		>
			<Link
				currentPath={isBrowser && window.location.pathname}
				to={`#anchor-${id}`}
				aria-label={children}
				className="anchor"
			>
				<svg
					aria-hidden="true"
					focusable="false"
					height="16"
					version="1.1"
					viewBox="0 0 16 16"
					width="16"
				>
					<path
						fillRule="evenodd"
						d="M4 9h1v1H4c-1.5 0-3-1.69-3-3.5S2.55 3 4 3h4c1.45 0 3 1.69 3 3.5 0 1.41-.91 2.72-2 3.25V8.59c.58-.45 1-1.27 1-2.09C10 5.22 8.98 4 8 4H4c-.98 0-2 1.22-2 2.5S3 9 4 9zm9-3h-1v1h1c1 0 2 1.22 2 2.5S13.98 12 13 12H9c-.98 0-2-1.22-2-2.5 0-.83.42-1.64 1-2.09V6.25c-1.09.53-2 1.84-2 3.25C6 11.31 7.55 13 9 13h4c1.45 0 3-1.69 3-3.5S14.5 6 13 6z"
					></path>
				</svg>
			</Link>
			{children}
		</h2>
	)
}
const H3 = ({ children }) => {
	const id = textToUrlPath(getHeadingText(children))
	return (
		<h3
			id={"anchor-" + id}
			className="group block -ml-10 pl-10"
		>
			<Link
				currentPath={isBrowser && window.location.pathname}
				to={`#anchor-${id}`}
				aria-label={children}
				className="anchor"
			>
				<svg
					aria-hidden="true"
					focusable="false"
					height="16"
					version="1.1"
					viewBox="0 0 16 16"
					width="16"
				>
					<path
						fillRule="evenodd"
						d="M4 9h1v1H4c-1.5 0-3-1.69-3-3.5S2.55 3 4 3h4c1.45 0 3 1.69 3 3.5 0 1.41-.91 2.72-2 3.25V8.59c.58-.45 1-1.27 1-2.09C10 5.22 8.98 4 8 4H4c-.98 0-2 1.22-2 2.5S3 9 4 9zm9-3h-1v1h1c1 0 2 1.22 2 2.5S13.98 12 13 12H9c-.98 0-2-1.22-2-2.5 0-.83.42-1.64 1-2.09V6.25c-1.09.53-2 1.84-2 3.25C6 11.31 7.55 13 9 13h4c1.45 0 3-1.69 3-3.5S14.5 6 13 6z"
					></path>
				</svg>
			</Link>
			{children}
		</h3>
	)
}

export default function RichText({ description, customClass }) {
	const [isModalOpen, setIsModalOpen] = React.useState(false)
	const [activeForm, setActiveForm] = React.useState(null)
	let inlineEntryCounter = 0

	const handleContentfulFormPopupClick = (form) => {
		if (!form || !form.contentfulfields) {
			console.error("Invalid form data:", form)
			return
		}
		setActiveForm(form)
		setIsModalOpen(true)
	}

	const options = {
		renderMark: {
			[MARKS.BOLD]: (text) => <Bold>{text}</Bold>,
		},
		renderText: (text) => {
			return text.split("\n").reduce((children, textSegment, index) => {
				return [...children, index > 0 && <br key={index} />, textSegment]
			}, [])
		},
		renderNode: {
			[BLOCKS.PARAGRAPH]: (node, children) => {
				return <p className="font-normal">{children}</p>
			},
			[BLOCKS.HEADING_2]: (node, children) => <H2>{children}</H2>,
			[BLOCKS.HEADING_3]: (node, children) => <H3>{children}</H3>,
			[BLOCKS.LIST_ITEM]: (node) => {
				const transformedChildren = node.content.map((childNode) => {
					return documentToReactComponents(
						{ nodeType: "document", data: {}, content: [childNode] },
						{
							renderMark: options.renderMark,
							renderNode: {
								...options.renderNode,
								[BLOCKS.PARAGRAPH]: (node, children) => {
									return <p className="font-normal m-0">{children}</p>
								},
								[BLOCKS.DOCUMENT]: (node, children) => children,
							},
						}
					)
				})
				return <li>{transformedChildren}</li>
			},
			[BLOCKS.TABLE]: (node, children) => {
				return (
					<table className="min-w-full divide-y divide-gray-300">
						<tbody className="divide-y divide-gray-200">{children}</tbody>
					</table>
				)
			},
			[BLOCKS.TABLE_HEADER_CELL]: (node, children) => {
				const transformedChildren = documentToReactComponents(node, {
					renderMark: options.renderMark,
					renderNode: {
						[BLOCKS.PARAGRAPH]: (node, children) => children,
						[BLOCKS.TABLE_HEADER_CELL]: (node, children) => children,
					},
				})

				return (
					<th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
						{transformedChildren}
					</th>
				)
			},
			[BLOCKS.TABLE_ROW]: (node, children) => <tr>{children}</tr>,
			[BLOCKS.TABLE_CELL]: (node, children) => {
				const transformedChildren = documentToReactComponents(node, {
					renderMark: options.renderMark,
					renderNode: {
						[BLOCKS.PARAGRAPH]: (node, children) => children,
						[BLOCKS.TABLE_CELL]: (node, children) => children,
					},
				})
				return (
					<td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
						{transformedChildren}
					</td>
				)
			},
			[INLINES.HYPERLINK]: (node, children) => {
				return (
					<Link
						to={node.data.uri}
						alt={node.content[0].value}
						currentPath={isBrowser && window.location.pathname}
					>
						{children}
					</Link>
				)
			},
			[BLOCKS.EMBEDDED_ASSET]: (node) => {
				if (!node.data.target || !description.references) return
				const isImage =
					(node.data.target.file.contentType === "image/jpeg") |
					(node.data.target.file.contentType === "image/png") |
					(node.data.target.file.contentType === "image/webp") |
					(node.data.target.file.contentType === "image/svg+xml") |
					(node.data.target.file.contentType === "image/gif")
						? true
						: false

				if (isImage) {
					return <Image image={node.data.target} />
				}
				if (node.data.target.file.contentType === "video/mp4") {
					return (
						<video controls>
							<source
								src={node.data.target.file.url}
								type="video/mp4"
							/>
							<track kind="captions" />
						</video>
					)
				}
				if (node.data.target.file.contentType === "text/html") {
					return (
						<iframe
							allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
							frameBorder="0"
							allowFullScreen
							type="text/html"
							className="w-full"
							title={node.data.target.title}
							src={node.data.target.file.url}
						/>
					)
				}
			},
			[BLOCKS.EMBEDDED_ENTRY]: (node) => {
				const target = node.data?.target

				// Handle standard embedded entries through target
				if (target) {
					switch (target.__typename) {
						case "ContentfulTextBlock":
							return renderRichText(target.richText, options)
						case "ContentfulQuestionsAndAnswers":
							return <Faq faq={target} />
						case "ContentfulCodeBlock":
							return (
								<pre>
									<code className={`language-${target.language}`}>{target.code.code}</code>
								</pre>
							)
						case "ContentfulVideo":
							return <VideoPlayer data={target} />
					}
				}

				// Handle form popups through references
				const targetId = node.data?.target?.sys?.id
				if (targetId && description.references) {
					const reference = description.references.find(
						(ref) => ref.__typename === "ContentfulFormPopup" && ref.contentful_id === targetId
					)

					if (reference) {
						return (
							<Button.BaseButton
								buttonStyle="fill"
								size="lg"
								onClick={() => handleContentfulFormPopupClick(reference.form)}
							>
								{reference.displayText || ""}
							</Button.BaseButton>
						)
					}
				}

				return null
			},
			[INLINES.EMBEDDED_ENTRY]: (node) => {
				const target = node.data?.target

				// Handle standard embedded entries through target
				if (target) {
					switch (target.__typename) {
						case "ContentfulTextBlock":
							return renderRichText(target.richText, options)
						case "ContentfulQuestionsAndAnswers":
							return <Faq faq={target} />
						case "ContentfulCodeBlock":
							return (
								<pre>
									<code className={`language-${target.language}`}>{target.code.code}</code>
								</pre>
							)
						case "ContentfulVideo":
							return <VideoPlayer data={target} />
					}
				}

				// Handle form popups through references
				const targetId = node.data?.target?.sys?.id
				if (targetId && description.references) {
					const reference = description.references.find(
						(ref) => ref.__typename === "ContentfulFormPopup" && ref.contentful_id === targetId
					)

					if (reference) {
						return (
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault()
									handleContentfulFormPopupClick(reference.form)
								}}
								onKeyDown={(e) => {
									if (e.key === "Enter" || e.key === " ") {
										e.preventDefault()
										handleContentfulFormPopupClick(reference.form)
									}
								}}
								role="button"
								tabIndex={0}
								className="text-primary-600 hover:text-primary-500 underline cursor-pointer"
							>
								{reference.displayText || "Click here"}
							</a>
						)
					}
				}

				return null
			},
			[BLOCKS.DOCUMENT]: (node, children) => <div>{children}</div>,
		},
	}

	return (
		<>
			<div className={customClass}>{description && renderRichText(description, options)}</div>

			{activeForm && (
				<FormModal
					isOpen={isModalOpen}
					setIsOpen={setIsModalOpen}
					form={activeForm}
				/>
			)}
		</>
	)
}
